/**
 * useVideoLibrary — manages video queue, saved videos, demo videos, and tab state.
 * Group B: Video Library (10 state variables, 3 useEffects)
 *
 * Does NOT include handleFileSelected or handleConversionConfirm — those are
 * orchestrators that cross multiple hook groups and stay in PrimateVision.tsx.
 */
import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';
import { getUserVideos, getDemoVideos, type UserVideo } from '../lib/api';
import { trackDemoQuotaHit } from '../telemetry/events';
import { type VideoEntry, type DemoVideoLibraryEntry } from './types';

export interface UseVideoLibraryReturn {
  videoLibraryTab: 'user' | 'demo' | 'stream';
  setVideoLibraryTab: (tab: 'user' | 'demo' | 'stream') => void;
  demoVideos: DemoVideoLibraryEntry[];
  demoVideosLoaded: boolean;
  setDemoVideosLoaded: (loaded: boolean) => void;
  userVideosLoaded: boolean;
  setUserVideosLoaded: (loaded: boolean) => void;
  savedUserVideos: UserVideo[];
  setSavedUserVideos: Dispatch<SetStateAction<UserVideo[]>>;
  selectedSavedVideo: UserVideo | null;
  setSelectedSavedVideo: (video: UserVideo | null) => void;
  deletingVideoId: string | null;
  setDeletingVideoId: (id: string | null) => void;
  suppressDeleteConfirm: boolean;
  setSuppressDeleteConfirm: (suppress: boolean) => void;
  videoQueue: VideoEntry[];
  setVideoQueue: Dispatch<SetStateAction<VideoEntry[]>>;
  activeVideoId: string | null;
  setActiveVideoId: (id: string | null) => void;
  DEMO_LIMIT: number;
  demoQuotaExceeded: boolean;
}

interface UseVideoLibraryOptions {
  isSignedIn: boolean;
  authLoaded: boolean;
  isAdmin: boolean;
  demoUsageCount: number;
}

export function useVideoLibrary({ isSignedIn, authLoaded, isAdmin, demoUsageCount }: UseVideoLibraryOptions): UseVideoLibraryReturn {
  const DEMO_LIMIT = 3;

  const [videoLibraryTab, setVideoLibraryTabState] = useState<'user' | 'demo' | 'stream'>(() => {
    const saved = sessionStorage.getItem('pv-video-tab');
    return (saved === 'user' || saved === 'demo' || saved === 'stream') ? saved : 'user';
  });
  const [demoVideos, setDemoVideos] = useState<DemoVideoLibraryEntry[]>([]);
  const [userVideosLoaded, setUserVideosLoaded] = useState(false);
  const [savedUserVideos, setSavedUserVideos] = useState<UserVideo[]>([]);
  const [selectedSavedVideo, setSelectedSavedVideo] = useState<UserVideo | null>(null);
  const [deletingVideoId, setDeletingVideoId] = useState<string | null>(null);
  const [suppressDeleteConfirm, setSuppressDeleteConfirm] = useState(() => localStorage.getItem('pv-suppress-delete-confirm') === '1');
  const [demoVideosLoaded, setDemoVideosLoaded] = useState(false);
  const [videoQueue, setVideoQueue] = useState<VideoEntry[]>([]);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  const demoQuotaExceeded = !isAdmin && !isSignedIn && demoUsageCount >= DEMO_LIMIT;

  // Load demo videos on mount (public, no auth required)
  useEffect(() => {
    if (demoVideosLoaded) return;
    // R15 (PRI-439): endpoint literal moved to the API layer.
    getDemoVideos()
      .then(data => {
        setDemoVideos((data.videos ?? []) as unknown as DemoVideoLibraryEntry[]);
        setDemoVideosLoaded(true);
      })
      .catch(() => setDemoVideosLoaded(true));
  }, [demoVideosLoaded]);

  // Load persisted user videos when signed in
  useEffect(() => {
    if (!isSignedIn || !authLoaded || userVideosLoaded) return;
    getUserVideos()
      .then(data => {
        setSavedUserVideos(data.videos ?? []);
        setUserVideosLoaded(true);
        // Note: we intentionally don't restore files into videoQueue here
        // because File objects can't be reconstructed from server data.
        // The saved results ARE available via savedUserVideos for display.
      })
      .catch(() => setUserVideosLoaded(true));
  }, [isSignedIn, authLoaded, userVideosLoaded]);

  // Video Library: default to Demo Videos tab for guests
  useEffect(() => {
    if (authLoaded) {
      const saved = sessionStorage.getItem('pv-video-tab');
      // Use saved tab if it makes sense (don't show user tab to guests)
      const tab: 'user' | 'demo' | 'stream' = (!isSignedIn && saved !== 'demo') ? 'demo' : ((saved === 'user' || saved === 'demo' || saved === 'stream') ? saved : (isSignedIn ? 'user' : 'demo'));
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVideoLibraryTabState(tab);
      sessionStorage.setItem('pv-video-tab', tab);
    }
  }, [authLoaded, isSignedIn]);

  // Track demo quota exceeded
  useEffect(() => {
    if (demoQuotaExceeded) trackDemoQuotaHit();
  }, [demoQuotaExceeded]);

  function setVideoLibraryTab(tab: 'user' | 'demo' | 'stream') {
    setVideoLibraryTabState(tab);
    sessionStorage.setItem('pv-video-tab', tab);
  }

  return {
    videoLibraryTab,
    setVideoLibraryTab,
    demoVideos,
    demoVideosLoaded,
    setDemoVideosLoaded,
    userVideosLoaded,
    setUserVideosLoaded,
    savedUserVideos,
    setSavedUserVideos,
    selectedSavedVideo,
    setSelectedSavedVideo,
    deletingVideoId,
    setDeletingVideoId,
    suppressDeleteConfirm,
    setSuppressDeleteConfirm,
    videoQueue,
    setVideoQueue,
    activeVideoId,
    setActiveVideoId,
    DEMO_LIMIT,
    demoQuotaExceeded,
  };
}
